import { Typography } from '../../../../core/components/molecules/Typography';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { DropDownList, IDropDownItems } from '../../../../core/components/molecules/DropDownList';
import { useWeekScheduleStore } from '../../stores/weekScheduleStore';
import { useEffect, useState } from 'react';
import { getDisciplineName } from '../../../../core/utils/discipline';
import { Button } from '../../../../core/components/atoms/Button';
import { toast } from '../../../../core/components/atoms/Toast';
import useGetDisciplineSubjects from '../../hooks/SelectSubjecsSchedule/useGetDisciplineSubjects';
import useAddSubjectsToSchedule from '../../hooks/SelectSubjecsSchedule/useAddSubjectToSchedule';
import ModalConfirmation from '../../components/SelectSubjectSchedule/ModalConfirmation';
import ModalHowTo from '../../components/SelectSubjectSchedule/ModalHowTo';
import SubmitFooter from '../../components/SelectSubjectSchedule/SubmitFooter';
import SubjectList from '../../components/SelectSubjectSchedule/SubjectList';
import SuccessModal from '../../components/SuccessModal';

export default function SelectSubjectsSchedule() {
  const navigate = useNavigate();
  const { scheduleId } = useParams();
  const [searchParams] = useSearchParams();

  const { weekScheduleDetailed } = useWeekScheduleStore();

  const { mutate: getSubjects, data: subjects, isPending: loadingSubjects, isError: isErrorSubjects } = useGetDisciplineSubjects();
  const { mutate: addSubjects, isPending: loadingAdd, isSuccess, isError } = useAddSubjectsToSchedule();
  
  const [selectedDiscipline, setSelectedDiscipline] = useState<IDropDownItems | undefined>();
  const [disciplines, setDisciplines] = useState<IDropDownItems[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [isHowToOpenModal, setIsHowToOpenModal] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  useEffect(() => {
    if(weekScheduleDetailed) {
      const disciplines = Object.keys(weekScheduleDetailed.disciplinas);
      
      const disciplinesList = disciplines.map((discipline) => ({
        id: discipline,
        title: getDisciplineName(discipline),
        subtitle: getDisciplineName(discipline),
      })).sort((a, b) => a.title.localeCompare(b.title));

      setDisciplines(disciplinesList);
      setSelectedDiscipline(disciplinesList[0]);
      getSubjects({ cronogramaId: scheduleId || '', disciplina: disciplinesList[0].id });
    }
  }, []);
  
  const selectDiscipline = (discipline: IDropDownItems) => {
    setSelectedDiscipline(discipline);
    getSubjects({ cronogramaId: scheduleId || '', disciplina: discipline.id });
  };

  const handleAddSubjects = () => {
    const dateQuery = searchParams.get('date');

    if (scheduleId && selectedSubjects.length > 0 && dateQuery) {
      const dateSplitted = dateQuery.split('/');
      const date = new Date(
        Number(dateSplitted[2]), 
        Number(dateSplitted[1]) - 1,
        Number(dateSplitted[0]), 
        0, 
        0, 
        0
      );

      addSubjects({
        cronogramaId: scheduleId,
        date,
        assuntos: selectedSubjects
      });
    }
  };
  
  useEffect(() => {
    if(isErrorSubjects) {
      toast({
        title: 'Erro ao buscar assuntos',
        message: 'Não foi possível buscar os assuntos desta disciplina',
        variant: 'error'
      });
    }
  }, [isErrorSubjects]);

  useEffect(() => {
    if(isError) {
      toast({
        title: 'Erro ao adicionar assuntos',
        message: 'Não foi possível adicionar os assuntos ao cronograma',
        variant: 'error'
      });
    }
  }, [isError]);

  useEffect(() => {
    if(isSuccess) {
      setIsConfirmationModalOpen(false);
      setIsSuccessModalOpen(true);
    }
  }, [isSuccess]);

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    setSelectedSubjects([]);
    navigate(`/plano-adaptativo/${scheduleId}/hub`);
  };

  return (
    <div className='flex flex-col gap-xxl px-2 py-8 md:px-10 lg:px-20'>
      <Breadcrumbs
        items={[
          {
            label: 'Cronograma',
            href: `/plano-adaptativo/${scheduleId}/hub`,
          },
          {
            label: 'Adicionar assuntos',
            href: '#',
          },
        ]}
      />

      <div className='flex flex-col'>
        <Typography type='title' size='h2'>
          Adicionar conteúdo
        </Typography>
        <Typography>
          Escolha a Disciplina e os Assuntos que gostaria de adicionar ao dia de estudo.
        </Typography>
      </div>

      <div className='flex flex-row items-center justify-between gap-lg'>
        <DropDownList
          items={disciplines}
          onSelect={selectDiscipline}
          value={selectedDiscipline}
        />
        <Button
          LeftIcon='info'
          variant='text'
          onClick={() => setIsHowToOpenModal(true)}
        >
          Como funciona?
        </Button>
      </div>

      <SubjectList
        isLoading={loadingSubjects}
        subjects={subjects || []}
        selectedSubjects={selectedSubjects}
        setSelectedSubjects={setSelectedSubjects}
      />

      <SubmitFooter
        handleSubmit={() => setIsConfirmationModalOpen(true)}
      />
      
     <ModalConfirmation
      isOpen={isConfirmationModalOpen}
      onClose={() => setIsConfirmationModalOpen(false)}
      loadingAdd={loadingAdd}
      handleConfirmation={handleAddSubjects}
      subjects={subjects}
      selectedSubjects={selectedSubjects}
     />

     <ModalHowTo
      isOpen={isHowToOpenModal}
      onClose={() => setIsHowToOpenModal(false)}
     />

     <SuccessModal
      isOpen={isSuccessModalOpen}
      onClose={handleCloseSuccessModal}
      title='Assunto(s) adicionado(s) com sucesso!'
     />
     
     
    </div>
  );
}
